import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./cohorts.page.module.scss";
import {
  getCohortGroups,
  createCohortGroup,
  getCohorts,
  createCohort,
  updateCohort,
  deleteCohort,
} from "apis";
import { ErrorCode } from "enums";

const COLOR_OPTIONS = [
  "#facc15",
  "#f59e0b",
  "#4ade80",
  "#16a34a",
  "#60a5fa",
  "#3b82f6",
  "#a855f7",
  "#ec4899",
  "#ef4444",
  "#06b6d4",
  "#84cc16",
  "#64748b",
  "#94a3b8",
];

const DEFAULT_COLOR = "#64748b";

// Derives the portal-side CH-XXX display code from the API integer id
const toCohortCode = (cohortId) => `CH-${String(cohortId).padStart(3, "0")}`;

const nextCohortCode = (cohorts) => {
  const max = cohorts.reduce((m, c) => Math.max(m, c.cohortId ?? 0), 0);
  return toCohortCode(max + 1);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CohortCard = React.memo(({ cohort, onEdit, onDelete }) => (
  <article className={styles.cohortCard}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitleWrap}>
        <span className={styles.cardDot} style={{ backgroundColor: cohort.color }} />
        <h3>{cohort.name}</h3>
      </div>
      <div className={styles.cardActions}>
        <button type="button" aria-label={`Edit ${cohort.name}`} onClick={onEdit}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
            />
          </svg>
        </button>
        <button type="button" aria-label={`Delete ${cohort.name}`} onClick={onDelete}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    <p className={styles.codePill}>{toCohortCode(cohort.cohortId)}</p>
    {cohort.description && <p className={styles.description}>{cohort.description}</p>}
  </article>
));

// ─── Page ─────────────────────────────────────────────────────────────────────

const CohortsPage = () => {
  const [groups, setGroups] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialog: null | 'addCohort' | 'editCohort' | 'deleteCohort' | 'addGroup'
  const [dialogType, setDialogType] = useState(null);
  const [activeCohort, setActiveCohort] = useState(null);

  const [cohortForm, setCohortForm] = useState({
    name: "",
    cohortGroupId: null,
    description: "",
    color: DEFAULT_COLOR,
  });
  const [groupForm, setGroupForm] = useState({ name: "" });

  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [groupNameError, setGroupNameError] = useState("");
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // ─── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsData, cohortsData] = await Promise.all([getCohortGroups(), getCohorts()]);
      setGroups(groupsData ?? []);
      setCohorts(cohortsData ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );

  // ─── Toast helper ─────────────────────────────────────────────────────────────

  const showToast = useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(msg);
    toastTimerRef.current = setTimeout(() => setToast(null), 4500);
  }, []);

  // ─── Grouped view ─────────────────────────────────────────────────────────────

  const groupedSections = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        cohorts: cohorts.filter((c) => c.cohortGroupId === g.cohortGroupId),
      })),
    [groups, cohorts],
  );

  const uncategorized = useMemo(() => cohorts.filter((c) => c.cohortGroupId == null), [cohorts]);

  const groupSelectOptions = useMemo(
    () => [
      { value: "", label: "Uncategorized" },
      ...groups.map((g) => ({ value: String(g.cohortGroupId), label: g.name })),
    ],
    [groups],
  );

  // ─── Dialog helpers ───────────────────────────────────────────────────────────

  const closeDialog = useCallback(() => {
    setDialogType(null);
    setActiveCohort(null);
    setNameError("");
    setGroupNameError("");
  }, []);

  const openAddCohort = useCallback(() => {
    setCohortForm({ name: "", cohortGroupId: null, description: "", color: DEFAULT_COLOR });
    setNameError("");
    setDialogType("addCohort");
  }, []);

  const openEditCohort = useCallback((cohort) => {
    setActiveCohort(cohort);
    setCohortForm({
      name: cohort.name,
      cohortGroupId: cohort.cohortGroupId ?? null,
      description: cohort.description ?? "",
      color: cohort.color,
    });
    setNameError("");
    setDialogType("editCohort");
  }, []);

  const openDeleteCohort = useCallback((cohort) => {
    setActiveCohort(cohort);
    setDialogType("deleteCohort");
  }, []);

  const openAddGroup = useCallback(() => {
    setGroupForm({ name: "" });
    setGroupNameError("");
    setDialogType("addGroup");
  }, []);

  // ─── Cohort form ──────────────────────────────────────────────────────────────

  const onCohortNameChange = (e) => {
    setNameError("");
    setCohortForm((prev) => ({ ...prev, name: e.target.value }));
  };

  const onGroupIdChange = (e) => {
    const val = e.target.value;
    setCohortForm((prev) => ({ ...prev, cohortGroupId: val === "" ? null : Number(val) }));
  };

  const onDescChange = (e) => {
    setCohortForm((prev) => ({ ...prev, description: e.target.value }));
  };

  const onColorSelect = (color) => {
    setCohortForm((prev) => ({ ...prev, color }));
  };

  const onSaveCohort = async (e) => {
    e.preventDefault();
    const name = String(cohortForm.name || "").trim();
    if (!name) return;

    setSaving(true);
    setNameError("");
    try {
      if (dialogType === "editCohort") {
        const updated = await updateCohort(activeCohort.cohortId, {
          name,
          color: cohortForm.color,
          description: cohortForm.description || null,
          cohortGroupId: cohortForm.cohortGroupId,
        });
        setCohorts((prev) => prev.map((c) => (c.cohortId === updated.cohortId ? updated : c)));
      } else {
        const created = await createCohort({
          name,
          color: cohortForm.color,
          description: cohortForm.description || undefined,
          cohortGroupId: cohortForm.cohortGroupId,
        });
        setCohorts((prev) => [...prev, created]);
      }
      closeDialog();
    } catch (err) {
      const code = err?.data?.errorCode ?? err?.data?.code;
      if (code === ErrorCode.CohortNameTaken) {
        setNameError(`A cohort named "${name}" already exists`);
        showToast(`A cohort named "${name}" already exists`);
      } else {
        setNameError("Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete cohort ────────────────────────────────────────────────────────────

  const onDeleteCohort = async () => {
    setSaving(true);
    try {
      await deleteCohort(activeCohort.cohortId);
      setCohorts((prev) => prev.filter((c) => c.cohortId !== activeCohort.cohortId));
      closeDialog();
    } catch {
      closeDialog();
    } finally {
      setSaving(false);
    }
  };

  // ─── Create group ─────────────────────────────────────────────────────────────

  const onCreateGroup = async (e) => {
    e.preventDefault();
    const name = String(groupForm.name || "").trim();
    if (!name) return;

    setSaving(true);
    setGroupNameError("");
    try {
      const created = await createCohortGroup({ name });
      setGroups((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      closeDialog();
    } catch (err) {
      const code = err?.data?.errorCode ?? err?.data?.code;
      if (code === ErrorCode.CohortGroupNameTaken) {
        setGroupNameError(`A group named "${name}" already exists`);
        showToast(`A group named "${name}" already exists`);
      } else {
        setGroupNameError("Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>Loading cohorts…</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>
            <span className={styles.headerIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                />
                <circle cx="9" cy="7" r="4" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            Cohorts
          </h1>
          <p>
            Buckets a respondent is tagged into when they pick a given answer. Available to every
            campaign&apos;s survey builder.
          </p>
        </div>

        <button type="button" className={styles.newButton} onClick={openAddCohort}>
          + New cohort
        </button>
      </header>

      {/* Named group sections from API */}
      {groupedSections.map((section) => (
        <section key={section.cohortGroupId} className={styles.groupSection}>
          <div className={styles.groupHeader}>
            <h2>{String(section.name).toUpperCase()}</h2>
            <span className={styles.groupCount}>{section.cohorts.length}</span>
          </div>
          <div className={styles.cardGrid}>
            {section.cohorts.map((cohort) => (
              <CohortCard
                key={cohort.cohortId}
                cohort={cohort}
                onEdit={() => openEditCohort(cohort)}
                onDelete={() => openDeleteCohort(cohort)}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Uncategorized bucket — rendered client-side, not a real group row */}
      {uncategorized.length > 0 && (
        <section className={styles.groupSection}>
          <div className={styles.groupHeader}>
            <h2>UNCATEGORIZED</h2>
            <span className={styles.groupCount}>{uncategorized.length}</span>
          </div>
          <div className={styles.cardGrid}>
            {uncategorized.map((cohort) => (
              <CohortCard
                key={cohort.cohortId}
                cohort={cohort}
                onEdit={() => openEditCohort(cohort)}
                onDelete={() => openDeleteCohort(cohort)}
              />
            ))}
          </div>
        </section>
      )}

      <button type="button" className={styles.addGroupButton} onClick={openAddGroup}>
        <span aria-hidden="true">+</span>
        <span aria-hidden="true" className={styles.folderIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"
            />
          </svg>
        </span>
        Add new group
      </button>

      {/* ── Modals ─────────────────────────────────────────────────────────────── */}
      {dialogType && (
        <div className={styles.modalOverlay} role="presentation" onClick={closeDialog}>
          {/* Delete cohort */}
          {dialogType === "deleteCohort" && (
            <div
              className={styles.deleteModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-cohort-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.deleteAlertIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12" />
                </svg>
              </div>
              <h3 id="delete-cohort-title">Delete &ldquo;{activeCohort?.name}&rdquo;?</h3>
              <p>
                {(activeCohort?.usageCount ?? 0) === 0
                  ? "It is not tagged to any answer option."
                  : `It is tagged to ${activeCohort.usageCount} answer option${activeCohort.usageCount !== 1 ? "s" : ""}.`}
              </p>
              <div className={styles.deleteModalActions}>
                <button type="button" className={styles.ghostBtn} onClick={closeDialog}>
                  Keep it
                </button>
                <button
                  type="button"
                  className={styles.dangerBtn}
                  onClick={onDeleteCohort}
                  disabled={saving}
                >
                  {saving ? "Deleting…" : "Delete cohort"}
                </button>
              </div>
            </div>
          )}

          {/* New group */}
          {dialogType === "addGroup" && (
            <form
              className={styles.editorModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="group-dialog-title"
              onClick={(e) => e.stopPropagation()}
              onSubmit={onCreateGroup}
            >
              <h3 id="group-dialog-title">New group</h3>
              <div className={`${styles.formField} ${styles.formFieldFirst}`}>
                <label htmlFor="group-name-input">Group name</label>
                <input
                  id="group-name-input"
                  type="text"
                  value={groupForm.name}
                  onChange={(e) => {
                    setGroupNameError("");
                    setGroupForm({ name: e.target.value });
                  }}
                  placeholder="e.g. Swing Cohorts"
                  autoFocus
                  className={groupNameError ? styles.inputError : undefined}
                />
                {groupNameError && <p className={styles.fieldError}>{groupNameError}</p>}
              </div>
              <div className={styles.editorActions}>
                <button type="button" className={styles.ghostBtn} onClick={closeDialog}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={!groupForm.name.trim() || saving}
                >
                  {saving ? "Creating…" : "Create group"}
                </button>
              </div>
            </form>
          )}

          {/* Add / Edit cohort */}
          {(dialogType === "addCohort" || dialogType === "editCohort") && (
            <form
              className={styles.editorModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cohort-dialog-title"
              onClick={(e) => e.stopPropagation()}
              onSubmit={onSaveCohort}
            >
              <h3 id="cohort-dialog-title">
                {dialogType === "editCohort" ? "Edit cohort" : "New cohort"}
              </h3>
              <p className={styles.modalLead}>
                Respondents are tagged into this bucket when they pick an answer you attach it to.
              </p>

              <div className={styles.formField}>
                <label>COHORT ID</label>
                <div className={styles.readOnlyField}>
                  {dialogType === "editCohort"
                    ? toCohortCode(activeCohort.cohortId)
                    : nextCohortCode(cohorts)}
                </div>
              </div>

              <div className={styles.formField}>
                <label htmlFor="cohort-name-input">Cohort name</label>
                <input
                  id="cohort-name-input"
                  type="text"
                  value={cohortForm.name}
                  onChange={onCohortNameChange}
                  placeholder="e.g. Soft Undecided"
                  autoFocus={dialogType === "addCohort"}
                  className={nameError ? styles.inputError : undefined}
                  required
                />
                {nameError && <p className={styles.fieldError}>{nameError}</p>}
              </div>

              <div className={styles.formField}>
                <label htmlFor="cohort-group-select">Group</label>
                <div className={styles.selectWrap}>
                  <select
                    id="cohort-group-select"
                    value={cohortForm.cohortGroupId == null ? "" : String(cohortForm.cohortGroupId)}
                    onChange={onGroupIdChange}
                  >
                    {groupSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formField}>
                <label htmlFor="cohort-desc-input">
                  Description <span>(optional)</span>
                </label>
                <input
                  id="cohort-desc-input"
                  type="text"
                  value={cohortForm.description}
                  onChange={onDescChange}
                  placeholder="What this bucket means for targeting"
                />
              </div>

              <div className={styles.formField}>
                <label>Colour</label>
                <div className={styles.colorPalette}>
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={
                        cohortForm.color === color ? styles.colorSwatchSelected : styles.colorSwatch
                      }
                      style={{ backgroundColor: color }}
                      onClick={() => onColorSelect(color)}
                      aria-label={`Select colour ${color}`}
                      aria-pressed={cohortForm.color === color}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.editorActions}>
                <button type="button" className={styles.ghostBtn} onClick={closeDialog}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.primaryBtn}
                  disabled={!cohortForm.name.trim() || saving}
                >
                  {saving ? "Saving…" : "Save cohort"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Bottom toast — uniqueness errors and other notifications */}
      {toast && (
        <div className={styles.cohortToast} role="status" aria-live="polite">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default CohortsPage;
